import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const CookiePolicy = () => {
    console.log(`[${new Date().toISOString()}] CookiePolicy component render`);
    const { t } = useTranslation();

    // Colors for consistent theming
    const primaryTextColor = "text-silver-light"; // Very light gray/silver
    const secondaryTextColor = "text-silver-medium"; // Medium gray/silver

    return (
        <div className="bg-black min-h-screen pt-24 pb-16">
            <SEO
                title="Cookie Policy | Riviera Tech Solutions"
                description="Read the Cookie Policy for Riviera Tech Solutions, including cookie types, usage, and browser control options."
                keywords="cookie policy, website cookies, cookie settings Riviera Tech, privacy cookies"
                url="/cookies"
            />
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    <h1 className={`text-4xl font-serif font-medium mb-6 ${primaryTextColor}`}>{t('legal.cookiePolicy.title')}</h1>
                    <p className={`${secondaryTextColor} mb-8`}>{t('legal.lastUpdated')}: {new Date().toLocaleDateString()}</p>

                    <div className="space-y-8">
                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>{t('legal.cookiePolicy.section1.heading')}</h2>
                            <div className={`${secondaryTextColor} space-y-4 text-sm leading-relaxed`}>
                                <p>
                                    {t('legal.cookiePolicy.section1.paragraph1')}
                                </p>
                                <p>
                                    {t('legal.cookiePolicy.section1.paragraph2')}
                                </p>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>{t('legal.cookiePolicy.section2.heading')}</h2>
                            <div className={`${secondaryTextColor} space-y-4 text-sm leading-relaxed`}>
                                <p>
                                    {t('legal.cookiePolicy.section2.paragraph1')}
                                </p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li><span className={primaryTextColor}>{t('legal.cookiePolicy.section2.types.essential.title')}:</span> {t('legal.cookiePolicy.section2.types.essential.description')}</li>
                                    <li><span className={primaryTextColor}>{t('legal.cookiePolicy.section2.types.performance.title')}:</span> {t('legal.cookiePolicy.section2.types.performance.description')}</li>
                                    <li><span className={primaryTextColor}>{t('legal.cookiePolicy.section2.types.functional.title')}:</span> {t('legal.cookiePolicy.section2.types.functional.description')}</li>
                                    <li><span className={primaryTextColor}>{t('legal.cookiePolicy.section2.types.targeting.title')}:</span> {t('legal.cookiePolicy.section2.types.targeting.description')}</li>
                                </ul>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>{t('legal.cookiePolicy.section3.heading')}</h2>
                            <div className={`${secondaryTextColor} space-y-4 text-sm leading-relaxed`}>
                                <p>
                                    {t('legal.cookiePolicy.section3.paragraph1')}
                                </p>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>{t('legal.cookiePolicy.section4.heading')}</h2>
                            <div className={`${secondaryTextColor} space-y-4 text-sm leading-relaxed`}>
                                <p>
                                    {t('legal.cookiePolicy.section4.paragraph1')}
                                </p>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>{t('legal.cookiePolicy.section5.heading')}</h2>
                            <div className={`${secondaryTextColor} space-y-4 text-sm leading-relaxed`}>
                                <p>
                                    {t('legal.cookiePolicy.section5.paragraph1')}
                                </p>
                                <p>
                                    {t('legal.cookiePolicy.section5.paragraph2')}
                                </p>
                                <p>
                                    {t('legal.cookiePolicy.section5.paragraph3')}
                                </p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li>{t('legal.cookiePolicy.section5.browsers.chrome')}: <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className={primaryTextColor}>https://support.google.com/chrome/answer/95647</a></li>
                                    <li>{t('legal.cookiePolicy.section5.browsers.firefox')}: <a href="https://support.mozilla.org/kb/delete-cookies-remove-info-websites-stored" target="_blank" rel="noopener noreferrer" className={primaryTextColor}>https://support.mozilla.org/kb/delete-cookies-remove-info-websites-stored</a></li>
                                    <li>{t('legal.cookiePolicy.section5.browsers.ie')}: <a href="https://support.microsoft.com/help/17442/windows-internet-explorer-delete-manage-cookies" target="_blank" rel="noopener noreferrer" className={primaryTextColor}>https://support.microsoft.com/help/17442/windows-internet-explorer-delete-manage-cookies</a></li>
                                    <li>{t('legal.cookiePolicy.section5.browsers.safari')}: <a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" className={primaryTextColor}>https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac</a></li>
                                </ul>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>{t('legal.cookiePolicy.section6.heading')}</h2>
                            <div className={`${secondaryTextColor} space-y-4 text-sm leading-relaxed`}>
                                <p>
                                    {t('legal.cookiePolicy.section6.paragraph1')}
                                </p>
                                <p>
                                    {t('legal.cookiePolicy.section6.paragraph2')}
                                </p>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>{t('legal.cookiePolicy.section7.heading')}</h2>
                            <div className={`${secondaryTextColor} space-y-4 text-sm leading-relaxed`}>
                                <p>
                                    {t('legal.cookiePolicy.section7.paragraph1')}
                                </p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li>{t('legal.cookiePolicy.section7.contactMethods.email', { email: 'support@rivieratech.solutions' })}</li>
                                    <li>{t('legal.cookiePolicy.section7.contactMethods.contact')}</li>
                                </ul>
                            </div>
                        </section>
                    </div>

                    <div className="mt-10 pt-6 border-t border-gray-800">
                        <p className="text-[11px] uppercase tracking-[0.16em] text-silver-dark mb-2">Related pages</p>
                        <p className="text-xs sm:text-sm text-silver-medium">
                            <Link to="/terms" className="text-silver-dark hover:text-silver-medium transition-colors">Terms of Service</Link>
                            {" · "}
                            <Link to="/privacy" className="text-silver-dark hover:text-silver-medium transition-colors">Privacy Policy</Link>
                            {" · "}
                            <Link to="/eula" className="text-silver-dark hover:text-silver-medium transition-colors">EULA</Link>
                            {" · "}
                            <Link to="/contact" className="text-silver-dark hover:text-silver-medium transition-colors">Contact</Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CookiePolicy;
